import {
  Card,
  FormControl,
  FormHelperText,
  OutlinedInput,
  Stack,
  CardContent,
  Typography,
  Box,
} from "@mui/material";
import React, { FC, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z as zod } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import LoadingButton from "@mui/lab/LoadingButton";
import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";

export interface InstagramSearchFormProps {
  value: string;
  hasGeneralError: boolean;
  hasUserNotFoundError: boolean;
  isLoading: boolean;
  onSubmit: (url: string) => Promise<void>;
  placeholderText: string;
  buttonText: string;
}

export const InstagramSearchForm: FC<InstagramSearchFormProps> = (props) => {
  const {
    onSubmit,
    value,
    hasGeneralError,
    hasUserNotFoundError,
    isLoading,
    placeholderText,
    buttonText,
  } = props;

  const { t } = useTranslation();

  const schema = zod.object({
    value: zod
      .string()
      .min(1, { message: t(tokens.general.validators.required) }),
  });
  const defaultValues = { value: value ?? "" } satisfies Values;

  type Values = zod.infer<typeof schema>;
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<Values>({ defaultValues, resolver: zodResolver(schema) });

  const handleFormSubmit = async (valueInput: string) => {
    await onSubmit(valueInput);
  };

  useEffect(() => {
    if (hasGeneralError) {
      setError("root", {
        type: "manual",
        message: t(tokens.general.formError),
      });
    } else if (hasUserNotFoundError) {
      setError("root", {
        type: "manual",
        message: t(tokens.instagram.errors.notFound),
      });
    }
  }, [hasGeneralError, hasUserNotFoundError, setError, t]);

  return (
    <>
      <form
        onSubmit={handleSubmit(async (data) => {
          await handleFormSubmit(data.value);
        })}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Card
            sx={{
              width: {
                xs: "100%",
                sm: "500px",
                md: "600px",
              },
            }}
          >
            <CardContent>
              <Stack
                spacing={2}
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                justifyContent={"center"}
                alignContent={"center"}
                alignItems={"flex-start"}
              >
                <Stack
                  sx={{
                    width: "100%",
                  }}
                  direction={"column"}
                  spacing={1}
                >
                  <Controller
                    control={control}
                    name="value"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.value)}>
                        {/* <InputLabel>Instagram username</InputLabel> */}
                        <OutlinedInput
                          placeholder={placeholderText}
                          size="medium"
                          fullWidth
                          sx={{
                            maxWidth: "100%",
                          }}
                          {...field}
                          type="text"
                        />
                        {errors.value ? (
                          <FormHelperText>
                            {errors.value.message}
                          </FormHelperText>
                        ) : null}
                      </FormControl>
                    )}
                  />
                  {errors.root ? (
                    <Typography
                      color="error"
                      variant="body2"
                    >
                      {errors.root.message}
                    </Typography>
                  ) : null}
                </Stack>
                <Box>
                  <LoadingButton
                    disabled={isLoading}
                    loading={isLoading}
                    type="submit"
                    variant="contained"
                  >
                    {buttonText}
                  </LoadingButton>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </form>
    </>
  );
};
